import React from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';

// General Safety & Policy Monitoring - Safety Categories
const SAFETY_CATEGORIES = [
  { code: 'S1', name: 'Violent Crimes', description: 'Content related to violent criminal activities' },
  { code: 'S2', name: 'Non-Violent Crimes', description: 'Content related to non-violent criminal activities' },
  { code: 'S3', name: 'Sex-Related Crimes', description: 'Content related to sexual crimes' },
  { code: 'S4', name: 'Child Sexual Exploitation', description: 'Content involving exploitation of children' },
  { code: 'S5', name: 'Defamation', description: 'Content that defames individuals or groups' },
  { code: 'S6', name: 'Specialized Advice', description: 'Financial, medical, or legal advice' },
  { code: 'S7', name: 'Privacy', description: 'Content violating privacy' },
  { code: 'S8', name: 'Intellectual Property', description: 'Content violating IP rights' },
  { code: 'S9', name: 'Indiscriminate Weapons', description: 'Content about weapons of mass destruction' },
  { code: 'S10', name: 'Hate', description: 'Hateful or discriminatory content' },
  { code: 'S11', name: 'Suicide & Self-Harm', description: 'Content promoting self-harm' },
  { code: 'S12', name: 'Sexual Content', description: 'Adult sexual content' },
  { code: 'S13', name: 'Elections', description: 'Content interfering with elections' },
];

interface HarmCategorySelectorProps {
  value: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
}

export const HarmCategorySelector: React.FC<HarmCategorySelectorProps> = ({
  value = [],
  onChange,
  disabled = false
}) => {
  const handleToggle = (code: string) => {
    if (value.includes(code)) {
      onChange(value.filter(c => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Ignored Safety Categories
        <Tooltip title="Select categories that should be ignored during safety analysis. Useful for APIs that provide legitimate professional advice (e.g., medical APIs can ignore 'Specialized Advice')">
          <Chip 
            label="?" 
            size="small" 
            sx={{ ml: 1, height: 20, fontSize: '0.7rem', cursor: 'help' }}
            tabIndex={0}
          />
        </Tooltip>
      </Typography>
      
      {value.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {value.length} {value.length === 1 ? 'category' : 'categories'} will be ignored during safety analysis
          </Typography>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
        <FormGroup>
          {SAFETY_CATEGORIES.map((category) => (
            <Tooltip key={category.code} title={category.description} placement="right">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.includes(category.code)}
                    onChange={() => handleToggle(category.code)}
                    disabled={disabled}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      <strong>{category.code}:</strong> {category.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.description}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
            </Tooltip>
          ))}
        </FormGroup>
      </Paper>

      {value.length === 13 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="warning.main">
            Warning: All categories are ignored. No safety analysis will be performed.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default HarmCategorySelector;

